/**
 * Calculadora da mesa: a expressao e montada inteira e so calculada no "=",
 * respeitando a ordem das operacoes (2 + 3 x 4 = 14, nao 20).
 *
 * A conta em si NAO e feita aqui: quem avalia e o `expr-eval` (MIT, sem
 * dependencias, sem `eval`), que ja resolve precedencia, parenteses, potencia
 * e unario. Este modulo faz o que e nosso: montar a expressao tecla a tecla,
 * sanear o que ficou pela metade e traduzir os simbolos do visor (× ÷ − √ ² %
 * π) para a sintaxe do avaliador.
 *
 * Tudo aqui e funcao pura sobre a string da expressao — a tela (CalculatorSheet)
 * so guarda essa string e chama estas funcoes.
 */
import { Parser } from 'expr-eval'

/** Traco de erro no visor: divisao por zero, raiz de negativo, conta invalida. */
export const ERRO = '—'

export const OPERADORES = ['+', '−', '×', '÷'] as const

const MAX_EXPRESSAO = 80

// Sem funcoes, atribuicoes ou variaveis: so aritmetica.
const parser = new Parser({
  operators: {
    add: true, subtract: true, multiply: true, divide: true,
    power: true, factorial: false, remainder: false,
    logical: false, comparison: false, concatenate: false,
    conditional: false, assignment: false, in: false,
  },
})

/** Termina em algo que ja e um valor (e nao em operador ou parentese aberto). */
export function terminaEmValor(exp: string): boolean {
  return /[\d)²%π]$/.test(exp)
}

/** Numero que esta sendo digitado no fim da expressao ('' se nao houver). */
export function numeroDigitado(exp: string): string {
  return exp.match(/\d+(?:\.\d*)?(?:e-?\d*)?$/)?.[0] ?? ''
}

/** Ultimo numero da expressao — e o que a linha de baixo do visor mostra. */
export function numeroAtual(exp: string): string {
  const numeros = exp.match(/\d+(?:\.\d*)?(?:e-?\d*)?/g)
  return numeros ? numeros[numeros.length - 1] : '0'
}

function parentesesAbertos(exp: string): number {
  return (exp.match(/\(/g) ?? []).length - (exp.match(/\)/g) ?? []).length
}

/**
 * Aplica uma tecla a expressao e devolve a expressao nova.
 *
 * Teclas: '0'-'9', '.', '+', '−', '×', '÷', '(', ')', '()' (abre ou fecha,
 * conforme o caso), '√', '²', '%', 'π', 'e' (notacao cientifica) e '⌫'.
 *
 * Multiplicacao implicita nunca e deixada para o avaliador: "2π" e "2(" viram
 * "2×π" e "2×(" aqui, porque e mais previsivel do que depender do parser.
 */
export function digitar(exp: string, tecla: string): string {
  if (tecla === '⌫') return exp.slice(0, -1)
  if (exp.length >= MAX_EXPRESSAO) return exp

  const ultimo = exp.slice(-1)
  const vezes = terminaEmValor(exp) ? '×' : ''

  if (/^[0-9]$/.test(tecla)) {
    // digito depois de ')', '²', '%' ou 'π' e multiplicacao implicita
    return exp + (/[)²%π]$/.test(exp) ? '×' : '') + tecla
  }

  if (tecla === '.') {
    const numero = numeroDigitado(exp)
    if (numero.includes('.') || numero.includes('e')) return exp
    return numero === '' ? exp + vezes + '0.' : exp + '.'
  }

  if ((OPERADORES as readonly string[]).includes(tecla)) {
    // dentro do expoente, o "menos" e o sinal dele: 3e−8
    if (ultimo === 'e' && tecla === '−') return exp + '-'
    // so o menos abre conta (numero negativo); os outros precisam de valor antes
    if (exp === '' || ultimo === '(') return tecla === '−' ? exp + '−' : exp
    // trocar de operacao substitui a anterior em vez de empilhar
    if ((OPERADORES as readonly string[]).includes(ultimo)) return exp.slice(0, -1) + tecla
    return exp + tecla
  }

  if (tecla === '(') return exp + vezes + '('
  if (tecla === ')') return parentesesAbertos(exp) > 0 && terminaEmValor(exp) ? exp + ')' : exp
  if (tecla === '()') {
    return parentesesAbertos(exp) > 0 && terminaEmValor(exp)
      ? exp + ')'
      : exp + vezes + '('
  }

  if (tecla === '√') return exp + vezes + '√('
  if (tecla === 'π') return exp + vezes + 'π'
  if (tecla === '²' || tecla === '%') return terminaEmValor(exp) ? exp + tecla : exp

  if (tecla === 'e') {
    if (!/\d$/.test(exp) || numeroDigitado(exp).includes('e')) return exp
    return exp + 'e'
  }

  return exp
}

/**
 * Fecha o que ficou pela metade: tira operador, ponto ou expoente solto no
 * fim, descarta parentese vazio e fecha os que ficaram abertos. Serve para o
 * "=" nao castigar quem parou de digitar no meio.
 */
export function sanear(exp: string): string {
  let s = exp
  let antes = ''
  while (s !== antes) {
    antes = s
    s = s.replace(/(?:[+−×÷.]|e-?|√?\()$/, '')
  }
  return s + ')'.repeat(Math.max(0, parentesesAbertos(s)))
}

/** Simbolos do visor -> sintaxe do avaliador. */
function traduzir(exp: string): string {
  return exp
    .replace(/×/g, '*')
    .replace(/÷/g, '/')
    .replace(/−/g, '-')
    .replace(/√/g, 'sqrt')
    .replace(/²/g, '^2')
    .replace(/%/g, '/100')
    .replace(/π/g, '(3.141592653589793)')
}

/**
 * Calcula a expressao. Devolve null quando nao da numero: conta vazia ou
 * invalida, divisao por zero (Infinity) e raiz de negativo (NaN).
 */
export function avaliar(exp: string): number | null {
  const limpa = sanear(exp)
  if (limpa === '') return null
  try {
    const r = parser.evaluate(traduzir(limpa))
    return typeof r === 'number' && Number.isFinite(r) ? r : null
  } catch {
    return null
  }
}

export function formatar(n: number): string {
  // apara erro binario (0.1+0.2) sem notacao cientifica desnecessaria
  return String(parseFloat(n.toPrecision(12)))
}

/** Resultado ja pronto para o visor. */
export function calcular(exp: string): string {
  const r = avaliar(exp)
  return r === null ? ERRO : formatar(r)
}

/** Expressao com respiro em volta dos operadores, para ler no visor. */
export function paraExibicao(exp: string): string {
  return exp.replace(/([+−×÷])/g, ' $1 ').replace(/\s+/g, ' ').trim()
}

/** Encaixa um numero (do historico) na expressao, com o × implicito se faltar. */
export function inserirNumero(exp: string, numero: string): string {
  return terminaEmValor(exp) ? `${exp}×${numero}` : exp + numero
}
